import { ActiveFiltersSummaryList } from "@/components/data-table/components/ActiveFiltersSummary/ActiveFiltersSummaryList";
import type { ActiveFilterDescriptor } from "@/components/data-table/utils/activeFilterDescriptors";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { FilterIcon } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ActiveFiltersBarDivider } from "./ActiveFiltersBarDivider";
import {
  ACTIVE_FILTERS_BAR_SEGMENT_CLASS,
  ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS,
} from "./activeFiltersBarShared";
import { ActiveFiltersBarSortStatusIcon } from "./ActiveFiltersBarSortStatusIcon";

interface ActiveFiltersBarMobileUnifiedStripProps {
  idleLabelClass: string;
  triggerInteractionsClass: string;
  activeGlyphClass: string;
  dividerClass: string;
  countLabel: string;
  noneFiltersLabel: string;
  descriptors: ActiveFilterDescriptor[];
  onRemoveColumnFilter: (columnId: string) => void;
  onClearAllFilters: () => void;
  getColumnVisibilityToggle?: (
    columnId: string,
  ) => { isVisible: boolean; onToggle: () => void } | undefined;
  sortSummaryLine: string | null;
  sortDirection: "asc" | "desc" | null;
  noneActiveSortingLabel: string;
  onClearSorting: () => void;
}

/**
 * Toolbar strip — mobile viewport: single control opens one bottom sheet for filters + sorting,
 * avoiding two separate touch targets in the strip.
 */
const ActiveFiltersBarMobileUnifiedStripComp = ({
  idleLabelClass,
  triggerInteractionsClass,
  activeGlyphClass,
  dividerClass,
  countLabel,
  noneFiltersLabel,
  descriptors,
  onRemoveColumnFilter,
  onClearAllFilters,
  getColumnVisibilityToggle,
  sortSummaryLine,
  sortDirection,
  noneActiveSortingLabel,
  onClearSorting,
}: ActiveFiltersBarMobileUnifiedStripProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const committedCount = descriptors.length;
  const hasDescriptors = committedCount > 0;
  const sortingActive = sortSummaryLine !== null;

  const stripTitle = useMemo(() => {
    const chunks: string[] = [];
    chunks.push(hasDescriptors ? countLabel : noneFiltersLabel);
    chunks.push(sortingActive ? sortSummaryLine : noneActiveSortingLabel);
    return chunks.join("\u2003·\u2003");
  }, [
    countLabel,
    hasDescriptors,
    noneActiveSortingLabel,
    noneFiltersLabel,
    sortSummaryLine,
    sortingActive,
  ]);

  const handleClearAllFilters = useCallback(() => {
    onClearAllFilters();
    setOpen(false);
  }, [onClearAllFilters]);

  const handleClearSorting = useCallback(() => {
    onClearSorting();
    setOpen(false);
  }, [onClearSorting]);

  const filtersSectionBody = (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-left text-sm font-semibold leading-tight text-foreground">
          {t("table.footer.badgeFiltersHeading")}
        </h3>
        <p className="mt-1.5 text-left text-xs leading-snug text-muted-foreground">
          {hasDescriptors ? countLabel : noneFiltersLabel}
        </p>
      </div>
      {hasDescriptors ? (
        <ActiveFiltersSummaryList
          descriptors={descriptors}
          onRemoveColumnFilter={onRemoveColumnFilter}
          onClearAll={handleClearAllFilters}
          getColumnVisibilityToggle={getColumnVisibilityToggle}
        />
      ) : null}
    </div>
  );

  const sortSectionBody = sortingActive ? (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-left text-sm font-semibold leading-tight text-foreground">
          {t("table.footer.badgeSortHeading")}
        </h3>
        <p className="mt-1.5 text-left text-xs leading-snug text-muted-foreground">
          {sortSummaryLine}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 w-full text-xs"
        onClick={handleClearSorting}
      >
        {t("table.clearSorting")}
      </Button>
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      <h3 className="text-left text-sm font-semibold leading-tight text-foreground">
        {t("table.footer.badgeSortHeading")}
      </h3>
      <p className="text-left text-sm leading-snug text-muted-foreground">
        {noneActiveSortingLabel}
      </p>
    </div>
  );

  const detailScroll = (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      {filtersSectionBody}
      <Separator />
      {sortSectionBody}
    </div>
  );

  const triggerButton = (
    <button
      type="button"
      title={stripTitle}
      className={cn(
        ACTIVE_FILTERS_BAR_SEGMENT_CLASS,
        ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS,
        "flex w-full min-w-0 shrink-0 items-center justify-start gap-2 outline-none transition-colors max-sm:flex-nowrap",
        triggerInteractionsClass,
      )}
    >
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5",
          !hasDescriptors && idleLabelClass,
        )}
      >
        <FilterIcon
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            hasDescriptors
              ? activeGlyphClass
              : cn("opacity-70", idleLabelClass),
          )}
        />
        {hasDescriptors ? (
          <span className="tabular-nums">{committedCount}</span>
        ) : (
          <span className="sr-only">{noneFiltersLabel}</span>
        )}
      </span>

      <ActiveFiltersBarDivider dividerClass={dividerClass} />

      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5",
          !sortingActive && idleLabelClass,
        )}
      >
        <ActiveFiltersBarSortStatusIcon
          sortDirection={sortingActive ? sortDirection : null}
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            sortingActive ? activeGlyphClass : cn("opacity-70", idleLabelClass),
          )}
        />
        {sortingActive ? (
          <span className="tabular-nums">1</span>
        ) : (
          <span className="sr-only">{noneActiveSortingLabel}</span>
        )}
      </span>
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{triggerButton}</SheetTrigger>
      <SheetContent
        side="bottom"
        className={cn(
          "flex max-h-[min(85dvh,560px)] flex-col gap-0 overflow-hidden rounded-t-xl border-0 p-0 pb-[env(safe-area-inset-bottom)]",
          "[&>button.absolute]:right-3 [&>button.absolute]:top-3.5",
        )}
      >
        <div className="shrink-0 border-b px-4 pb-3 pt-4 pr-12">
          <SheetTitle className="text-left text-base font-semibold leading-tight">
            {t("table.footer.badgeSheetTitle")}
          </SheetTitle>
        </div>
        {detailScroll}
      </SheetContent>
    </Sheet>
  );
};

export const ActiveFiltersBarMobileUnifiedStrip = memo(
  ActiveFiltersBarMobileUnifiedStripComp,
);
